import { graphql } from "gatsby";
import React, { useMemo } from "react";
import { map, isArray, find, isEmpty, some, reduce } from "lodash";

import Header from "~/components/Header";

import { useLocationContext } from "~/context/LocationContext";
import { useTranslationContext } from "~/context/TranslationContext";

import logoSVG from "~/assets/images/logo-color.svg";

import {
    sortLanguageLinks,
    addIsActiveFlag,
    useAddQueryParams,
} from "~/context/TranslationContext";

import { isBrowser } from "~/utilities/helpers";
import { languageLabel } from "~/utilities/translations";

const useGetNavPermutations = (urlPermutations, navigation, activeCode) => {
    const { location } = useLocationContext();

    const englishPathname = find(urlPermutations, {
        languageCode: "en",
    })?.linkUrl;

    const pathname = englishPathname ?? location.pathname;

    return reduce(
        navigation?.nodes,
        (acc, nav) => {
            const languageCode = nav.languageCode;
            const linkLabel = languageLabel(
                nav.language.native_name,
                nav.language.native_name,
                nav.language.translated_name,
                nav.languageCode,
                activeCode,
            );

            if (some(urlPermutations, { languageCode })) {
                return acc;
            }

            return [
                ...acc,
                {
                    linkNativeName: nav.language.native_name,
                    linkTranslatedName: nav.language.translatedName,
                    linkLabel,
                    languageCode,
                    linkUrl: pathname,
                },
            ];
        },
        [],
    );
};

const useParseUrlData = (urlPermutations, navigation) => {
    const { activeCode } = useTranslationContext();

    const navPermutations = useGetNavPermutations(
        urlPermutations,
        navigation,
        activeCode,
    );

    const withQueryParams = useAddQueryParams([
        ...urlPermutations,
        ...navPermutations,
    ]);

    const withActiveState = addIsActiveFlag(withQueryParams, activeCode);

    return sortLanguageLinks(withActiveState);
};

const useHeaderTranslator = (navigation, pageCode) => {
    const { activeCode } = useTranslationContext();

    const translatedHeader = find(navigation?.nodes, {
        languageCode: isBrowser ? activeCode : pageCode,
    });

    const englishHeader = find(navigation?.nodes, { languageCode: "en" });

    const header = isEmpty(translatedHeader) ? englishHeader : translatedHeader;

    return header;
};

const useParseHeader = (navigation, pageCode) => {
    const header = useHeaderTranslator(navigation, pageCode);

    const contactLink = header?.components?.contact;
    const searchPlaceholder = header?.components?.searchPlaceholder || "";
    const searchLabel = header?.components?.searchLabel || "";

    const mainMenuItems = useMemo(() => {
        const createMenuItem = (menuItems) => {
            return map(
                menuItems,
                (
                    {
                        linkLabel,
                        linkUrl,
                        newTabOverride,
                        subNavItems,
                        dropdownLabel,
                    },
                    index,
                ) => {
                    const currentLevelMenuItems = {
                        id: `${linkLabel}-${index}`,
                        linkLabel,
                        linkUrl,
                        newTabOverride,
                        subItems: [],
                        dropdownLabel,
                    };

                    if (isArray(subNavItems) && subNavItems.length) {
                        const nestedMenuItems = createMenuItem(subNavItems);
                        currentLevelMenuItems.subItems = nestedMenuItems;
                    }
                    return currentLevelMenuItems;
                },
            );
        };

        return createMenuItem(header?.components?.mainMenu);
    }, [header?.components?.mainMenu]);

    return { contactLink, searchPlaceholder, mainMenuItems, searchLabel };
};

const HeaderBlock = ({ data, urlPermutations, pageCode }) => {
    const { navigation } = data;

    const parsed = useParseUrlData(urlPermutations, navigation);

    const headerData = useParseHeader(navigation, pageCode);

    const logo = {
        sourceUrl: logoSVG,
        altText: "Wolfspeed Logo",
        title: "Wolfspeed",
        width: "140",
        height: "45",
    };

    return (
        <Header
            {...{
                logo,
                urlPermutations: parsed,
                ...headerData,
            }}
        />
    );
};

export default HeaderBlock;

export const query = graphql`
    query HeaderQuery {
        navigation: allWpNavigation {
            nodes {
                languageCode
                language {
                    native_name
                    translated_name
                    language_code
                }
                components {
                    mainMenu {
                        dropdownLabel
                        linkLabel
                        linkUrl
                        newTabOverride
                        subNavItems {
                            linkLabel
                            linkUrl
                            newTabOverride
                            subNavItems {
                                linkLabel
                                linkUrl
                                newTabOverride
                            }
                        }
                    }
                    contact {
                        linkLabel
                        linkUrl
                    }
                    searchPlaceholder
                    searchLabel
                }
            }
        }
    }
`;
